/**
 * ClickChutney Simple Functions
 * No hooks, just simple functions that work everywhere
 */

import type { TrackingEvent, UserTraits } from './types';

function getTrack() {
  if (typeof window === 'undefined') {
    return () => {};
  }

  if (!(window as any).cc) {
    (window as any).cc = function(...args: any[]) {
      ((window as any).cc.q = (window as any).cc.q || []).push(args);
    };
    (window as any).cc.q = [];
  }

  return (window as any).cc;
}

/**
 * Simple tracking function - no hooks needed
 */
export function track(eventName: string, properties: TrackingEvent = {}) {
  getTrack()('event', eventName, properties);
}

/**
 * Simple pageview function
 */
export function pageview(page?: string, title?: string, properties: TrackingEvent = {}) {
  const props = { ...properties };
  if (title) {
    props.title = title;
  }
  getTrack()('pageview', page || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''), props);
}

/**
 * Simple identify function
 */
export function identify(userId: string, traits: UserTraits = {}) {
  getTrack()('identify', userId, traits);
}

// Legacy hook exports for compatibility (but they don't use hooks anymore)
export function useClickChutney() {
  return {
    track,
    pageview,
    identify,
    isReady: typeof window !== 'undefined' && !!(window as any).__cc?.initialized,
    error: null
  };
}

export function useEventTracking() {
  const trackClick = (elementName: string, properties: TrackingEvent = {}) => {
    track(EVENTS.CLICK, { element: elementName, ...properties });
  };

  const trackFormSubmit = (formName: string, properties: TrackingEvent = {}) => {
    track(EVENTS.FORM_SUBMIT, { form: formName, ...properties });
  };

  const trackPurchase = (orderId: string, revenue: number, properties: TrackingEvent = {}) => {
    track(EVENTS.PURCHASE, { orderId, revenue, ...properties });
  };

  const trackSignup = (method?: string, properties: TrackingEvent = {}) => {
    track(EVENTS.SIGNUP, { method, ...properties });
  };

  const trackLogin = (method?: string, properties: TrackingEvent = {}) => {
    track(EVENTS.LOGIN, { method, ...properties });
  };

  const trackSearch = (query: string, resultsCount?: number, properties: TrackingEvent = {}) => {
    track(EVENTS.SEARCH, { query, resultsCount, ...properties });
  };

  const trackError = (message: string, stack?: string, properties: TrackingEvent = {}) => {
    track(EVENTS.ERROR, { message, stack, ...properties });
  };

  return {
    track,
    trackClick,
    trackFormSubmit,
    trackPurchase,
    trackSignup,
    trackLogin,
    trackSearch,
    trackError
  };
}

export function usePageTracking() {
  // Does nothing - pageview is tracked automatically
  return { pageview };
}

export function useUserTracking() {
  const reset = () => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('__cc_vid');
      } catch {}
      (window as any).__cc = {};
    }
  };
  return { identify, reset };
}

export interface FormSubmitProps extends TrackingEvent {
  form: string;
  formData?: Record<string, any>;
}

export function useFormTracking(formName: string, options?: { includeFormData?: boolean; properties?: TrackingEvent }) {
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    let props: FormSubmitProps = { form: formName, ...options?.properties };

    if (options?.includeFormData) {
      const formData = new FormData(event.currentTarget);
      const data: Record<string, any> = {};
      formData.forEach((value, key) => {
        if (key in data) {
          if (!Array.isArray(data[key])) {
            data[key] = [data[key]];
          }
          data[key].push(value);
        } else {
          data[key] = value;
        }
      });
      props = { ...props, formData: data };
    }

    track(EVENTS.FORM_SUBMIT, props);
  };
  return { handleSubmit };
}

export function useSession() {
  if (typeof window === 'undefined') {
    return {
      visitorId: null,
      sessionId: null,
      pageviews: 0,
      landingPage: null
    };
  }
  
  const __cc = (window as any).__cc || {};
  
  return {
    visitorId: __cc.visitorId || null,
    sessionId: __cc.sessionId || 'session_' + Date.now() + Math.random().toString(36).slice(2),
    pageviews: __cc.pageviews || 1,
    landingPage: __cc.landingPage || window.location.pathname + window.location.search
  };
}

// Event constants
export const EVENTS = {
  CLICK: 'click',
  PAGEVIEW: 'pageview',
  FORM_SUBMIT: 'form_submit',
  PURCHASE: 'purchase',
  SIGNUP: 'signup',
  LOGIN: 'login',
  SEARCH: 'search',
  ERROR: 'error',
  ADD_TO_CART: 'add_to_cart',
  VIEW_CONTENT: 'view_content'
} as const;